'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseListTlgRealtimeDto,
  TlgRealtimeDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';
import { StoreGaugeCard } from './store-gauge-card';

export function TlgRealtime() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [realtimeData, setRealtimeData] = useState<TlgRealtimeDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // TLG 실시간 데이터 가져오기 함수
  const fetchTlgRealtimeData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      const params = new URLSearchParams();
      storeCodeList.forEach((code: string) =>
        params.append('storeCodeList', code)
      );

      const response = await apiClient.get(`stock/tlg-realtime`, {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListTlgRealtimeDto;

      if (data.result) {
        setRealtimeData(data.result);
      } else {
        toast({
          title: '오류',
          description: data.message || '데이터 조회에 실패했습니다.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('TLG realtime data fetch error:', error);
      toast({
        title: '오류',
        description: '데이터 조회 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 검색 핸들러
  const handleSearch = () => {
    fetchTlgRealtimeData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchTlgRealtimeData();
  }, []);

  // Store별로 데이터 그룹화
  const groupedByStore = realtimeData.reduce(
    (acc, item) => {
      const storeKey = item.storeName || item.storeCd || 'Unknown';
      if (!acc[storeKey]) {
        acc[storeKey] = [];
      }
      acc[storeKey].push(item);
      return acc;
    },
    {} as Record<string, TlgRealtimeDto[]>
  );

  return (
    <div>
      {/* TLG 실시간 데이터 그리드 */}
      <ExpandableCard
        title='TLG 게이지 실시간'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('tlg-realtime')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        onExpandChange={setIsExpanded}
      >
        <div
          className={`border rounded-lg ${isExpanded ? 'h-full' : 'h-[300px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
                grouping: { enabled: true },
              }}
              rows={realtimeData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tankNo'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'oilCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'logTime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'sensorHei'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sensorTemp'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'waterHei'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'waterVol'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'liquidQty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'stockQty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'drum'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkTotal'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkPercent'}
                dataType={RealGrid.ValueType.NUMBER}
                valueExpression="values['liquidQty'] / values['tkTotal'] * 100"
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '매장' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'tankNo'}
                header={{ text: '탱크번호' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'sensorHei'}
                header={{ text: '센서높이(mm)' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'sensorTemp'}
                header={{ text: '센서온도' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'waterHei'}
                header={{ text: '수분높이(mm)' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'waterVol'}
                header={{ text: '수분량(kg)' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'liquidQty'}
                header={{ text: '액체량(l)' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'stockQty'}
                header={{ text: '재고량(kg)' }}
                width={90}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'tkTotal'}
                header={{ text: '탱크용량(l)' }}
                width={65}
                numberFormat='#,##0.###'
              />
              <RGDataColumn
                fieldName={'drum'}
                header={{ text: 'DRUM/TON' }}
                numberFormat='#,##0.#'
              />
              <RGDataColumn
                fieldName={'tkPercent'}
                header={{ text: '(%)' }}
                width={120}
                numberFormat='#,##0.0'
                renderer={{
                  type: 'bar',
                  minimum: 0,
                  maximum: 100,
                  showLabel: true,
                }}
              />
              <RGDataColumn
                fieldName={'logTime'}
                header={{ text: '기록시간' }}
                width={150}
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>
      <div className='space-y-3 p-3'>
        {Object.entries(groupedByStore).map(([storeName, tanks]) => (
          <StoreGaugeCard key={storeName} storeName={storeName} tanks={tanks} />
        ))}
        {Object.keys(groupedByStore).length === 0 && (
          <div className='text-center py-8 text-gray-500'>
            데이터가 없습니다. 조회 버튼을 클릭하여 데이터를 불러오세요.
          </div>
        )}
      </div>
    </div>
  );
}
